/**
 * CopyField — a read-only value row with a copy button (endpoints, keys).
 * CodeBlock — a dark, copyable code panel with an optional eyebrow label.
 */
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface CopyFieldProps {
  value: string;
  /** Render the value in the monospace face. */
  mono?: boolean;
  className?: string;
}

export function CopyField({ value, mono, className }: CopyFieldProps) {
  function copy() {
    void navigator.clipboard.writeText(value);
    toast.success('Copied');
  }
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <div
        className={cn(
          'flex h-11 flex-1 items-center overflow-x-auto whitespace-nowrap rounded-md border border-input px-3.5 text-[13px] text-label',
          mono && 'font-mono',
        )}
      >
        {value}
      </div>
      <Button variant="outline" size="icon" type="button" onClick={copy} aria-label="Copy">
        <Copy className="h-4 w-4" />
      </Button>
    </div>
  );
}

export interface CodeBlockProps {
  /** Optional uppercase eyebrow (e.g. "Claude Code — CLI"). */
  label?: string;
  children: string;
  className?: string;
}

export function CodeBlock({ label, children, className }: CodeBlockProps) {
  function copy() {
    void navigator.clipboard.writeText(children);
    toast.success('Copied');
  }
  return (
    <div className={className}>
      {label && (
        <div className="mb-1.5 font-mono text-[11px] uppercase tracking-[0.06em] text-faint">
          {label}
        </div>
      )}
      <div className="relative rounded-md bg-primary p-4">
        <button
          type="button"
          onClick={copy}
          aria-label="Copy"
          className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-md text-faint transition-colors hover:bg-white/10"
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
        <pre className="m-0 overflow-x-auto pr-8 font-mono text-[12.5px] leading-relaxed text-[#e8e8ea]">
          {children}
        </pre>
      </div>
    </div>
  );
}
